import { useCallback, useEffect, useMemo, useRef } from 'react'
import throttle from 'lodash/throttle'

export function useResizeObserver(
  element: Element | null | undefined,
  callback: ResizeObserverCallback,
  wait = 100,
) {
  const cbRef = useRef(callback)

  useEffect(() => {
    cbRef.current = callback
  }, [callback])

  const debounced = useMemo(
    () =>
      throttle<ResizeObserverCallback>((entries, observer) => {
        cbRef.current(entries, observer)
      }, wait),
    [wait],
  )

  const observer = useMemo(() => new ResizeObserver(debounced), [wait])

  useEffect(() => {
    if (!element) {
      return
    }

    observer.observe(element)

    return () => {
      debounced.cancel()
      observer.disconnect()
    }
  }, [observer, element])
}

export function useWidthResizeObserver(
  element: Element | null | undefined,
  callback: (width: number) => void,
  wait = 100,
) {
  useDirectionalResizeObserver(element, 'width', callback, wait)
}

export function useHeightResizeObserver(
  element: Element | null | undefined,
  callback: (height: number) => void,
  wait = 100,
) {
  useDirectionalResizeObserver(element, 'height', callback, wait)
}

function useDirectionalResizeObserver(
  element: Element | null | undefined,
  direction: 'width' | 'height',
  callback: (size: number) => void,
  wait = 100,
) {
  const prev = useRef(0)
  const cbRef = useRef(callback)

  useEffect(() => {
    cbRef.current = callback
  }, [callback])

  const resizeHandler = useCallback<ResizeObserverCallback>(
    entries => {
      if (!element) {
        return
      }

      for (const entry of entries) {
        if (entry.target === element) {
          const current = entry.contentRect[direction]

          if (current != prev.current) {
            prev.current = current
            cbRef.current(current)
          }
        }
      }
    },
    [element],
  )

  useResizeObserver(element, resizeHandler, wait)
}
